// ==================== WHATSAPP STATUS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  Phone, 
  Settings,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { staggerItem } from '../../../utils/animations';
import type { DashboardOverviewStats } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface WhatsAppStatusProps {
  stats: DashboardOverviewStats | null;
  isLoading?: boolean;
}

// ==================== QUALITY RATING CONFIG ====================
// Reserved for future use when qualityRating is added to the schema
// const QUALITY_CONFIG: Record<string, { color: string; label: string }> = {
//   GREEN: { color: 'text-green-400', label: 'High' },
//   YELLOW: { color: 'text-yellow-400', label: 'Medium' },
//   RED: { color: 'text-red-400', label: 'Low' },
// };

// ==================== COMPONENT ====================

export const WhatsAppStatus = ({ stats, isLoading = false }: WhatsAppStatusProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">WhatsApp Account</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 animate-pulse">
              <div className="h-10 bg-(--color-border-gray) rounded" />
              <div className="h-6 bg-(--color-border-gray) rounded" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const whatsapp = stats?.whatsapp;
  const isConnected = whatsapp?.connected;

  if (!isConnected) {
    return (
      <motion.div variants={staggerItem}>
        <Card className="border-yellow-500/30">
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">WhatsApp Account</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="text-center py-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-2">
                <AlertTriangle size={20} className="text-yellow-400" />
              </div>
              <p className="text-sm text-white font-medium mb-0.5">Not Connected</p>
              <p className="text-xs text-(--color-muted-gray) mb-3">
                Connect your WhatsApp Business account to start sending messages
              </p>
              <Link to="/app/settings/whatsapp">
                <Button variant="primary" size="sm">
                  <Settings size={14} />
                  Connect WhatsApp
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Quality config available for future use when qualityRating is added
  // const qualityConfig = QUALITY_CONFIG[whatsapp.qualityRating || 'GREEN'] || QUALITY_CONFIG.GREEN;

  return (
    <motion.div variants={staggerItem}>
      <Card className="border-green-500/20">
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">WhatsApp Account</CardTitle>
            <Badge variant="success" className="gap-1">
              <CheckCircle2 size={10} />
              Connected
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-2">
            {/* Phone Number */}
            <div className="flex items-center gap-2 p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                <Phone size={14} className="text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-(--color-muted-gray)">Phone Number</p>
                <p className="text-xs font-medium text-white truncate">
                  {whatsapp.phoneNumber || 'N/A'}
                </p>
              </div>
            </div>

            {/* Settings Link */}
            <Link to="/app/settings/whatsapp" className="block">
              <Button variant="secondary" size="sm" className="w-full text-xs">
                <Settings size={12} />
                Manage Settings
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WhatsAppStatus;
